import { useEffect, useState } from "react";
import { adminCall } from "@/lib/adminApi";
import { labelForNeed } from "@/features/diagnostic/config";
import { EXPERIENCE, EXPERTISE, TRACKS } from "@/pages/Careers";
import { PrimaryButton } from "@/components/site/Primitives";
import { inputClass } from "@/components/forms/Fields";
import { LEAD_STATUS, TALENT_STATUS, label } from "./AdminQueue";

const Item = ({ k, children }) => (
  <div className="grid gap-1 border-t border-fg/10 py-3 sm:grid-cols-[180px_1fr]">
    <dt className="text-[12px] uppercase tracking-[0.12em] text-subtle">{k}</dt>
    <dd className="whitespace-pre-wrap break-words text-[14px] text-fg">{children || "—"}</dd>
  </div>
);

function LeadFields({ r }) {
  return (
    <>
      <Item k="Contact">{r.contact.full_name} · {r.contact.email}</Item>
      <Item k="Organization">{r.contact.organization}{r.contact.organization_type ? ` (${r.contact.organization_type})` : ""} · {r.contact.country}</Item>
      <Item k="Business need">{r.need.text}</Item>
      <Item k="Selected areas">{r.need.need_ids.map(labelForNeed).join(", ")}</Item>
      <Item k="Successful outcome">{r.follow_up?.answers?.["successful-outcome"]}</Item>
      <Item k="Request sent">{r.follow_up?.submitted_at ? new Date(r.follow_up.submitted_at).toLocaleString() : "Not yet"}</Item>
      <Item k="Callback">{r.callback.requested ? `${r.callback.phone}${r.callback.preferred_time ? ` · ${r.callback.preferred_time}` : ""}${r.callback.timezone ? ` · ${r.callback.timezone}` : ""}` : "Not requested"}</Item>
      <Item k="Source / date">{r.source} · {new Date(r.created_at).toLocaleString()}</Item>
    </>
  );
}

function TalentFields({ r }) {
  return (
    <>
      <Item k="Applicant">{r.contact.full_name} · {r.contact.email}</Item>
      <Item k="Track">{label(TRACKS, r.track)} applicant (not hired)</Item>
      <Item k="Country of residence">{r.country_of_residence}</Item>
      <Item k="Expertise">{r.expertise.map((e) => label(EXPERTISE, e)).join(", ")}{r.expertise_other ? ` · ${r.expertise_other}` : ""}</Item>
      <Item k="Experience">{label(EXPERIENCE, r.experience_band)}</Item>
      <Item k="Profile">{r.profile_url && <a href={r.profile_url} target="_blank" rel="noopener noreferrer nofollow" className="underline underline-offset-4">{r.profile_url}</a>}</Item>
      <Item k="Note">{r.note}</Item>
      <Item k="Source / date">{r.source} · {new Date(r.created_at).toLocaleString()}</Item>
    </>
  );
}

export default function RecordDetail({ tab, id, onBack, onSignedOut }) {
  const [r, setR] = useState(null);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let alive = true;
    adminCall("get", `/${tab}/${id}`).then((res) => {
      if (!alive) return;
      if (res.status === 401) return onSignedOut();
      if (!res.ok) return setMsg(res.message);
      setR(res.data); setStatus(res.data.status); setNote(res.data.admin?.note || "");
      adminCall("post", "/notifications/read", { record_id: id });
    });
    return () => { alive = false; };
  }, [tab, id, onSignedOut]);

  const save = async () => {
    setPending(true);
    const res = await adminCall("patch", `/${tab}/${id}`, { status, note });
    setPending(false);
    if (res.status === 401) return onSignedOut();
    setMsg(res.ok ? "Saved." : res.message);
    if (res.ok) setR(res.data);
  };

  return (
    <div data-testid="admin-detail" className="max-w-3xl">
      <button type="button" onClick={onBack} data-testid="admin-detail-back" className="fa-link rounded-sm text-[13px] text-subtle hover:text-fg">← Back to list</button>
      {r && (
        <>
          <dl className="mt-6">{tab === "leads" ? <LeadFields r={r} /> : <TalentFields r={r} />}</dl>
          <div className="mt-8 grid gap-4">
            <label className="text-[13px]">Status
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-status-select" className={`${inputClass} mt-2 capitalize`}>
                {(tab === "leads" ? LEAD_STATUS : TALENT_STATUS).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="text-[13px]">Private note
              <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} rows={4} data-testid="admin-note" className={`${inputClass} mt-2`} />
            </label>
            <div className="flex items-center gap-4">
              <PrimaryButton onClick={save} disabled={pending} data-testid="admin-save">{pending ? "Saving…" : "Save"}</PrimaryButton>
              <span role="status" className="text-[13px] text-subtle" data-testid="admin-save-status">{msg}</span>
            </div>
          </div>
        </>
      )}
      {!r && msg && <p role="alert" className="mt-6 text-danger">{msg}</p>}
    </div>
  );
}
